import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { hasGranularPermission } from '../lib/permissions';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CurrencyInput } from './ui/currency-input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import GridPagination from './GridPagination';
import useClientPagination from '../hooks/useClientPagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Progress } from './ui/progress';
import { validateDateBR } from '../lib/helpers';
import { 
  FileText as FileContract,
  Plus,
  Edit,
  Trash2,
  Eye,
  Send,
  Search,
  Filter,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  FileSignature,
  Users,
  Building,
  TrendingUp,
  AlertCircle,
  Download,
  Tag,
  Bell,
  BellRing,
  FileUp,
  FileDown,
  History,
  Shield,
  Star,
  Briefcase,
  Calendar as CalendarIcon,
  MapPin,
  Phone,
  Mail,
  FileText,
  PieChart,
  BarChart3,
  Activity,
  Award,
  Target,
  Zap,
  Globe,
  Lock,
  Unlock,
  Copy,
  ExternalLink,
  Archive,
  RotateCcw,
  PlayCircle,
  PauseCircle,
  StopCircle,
  LayoutGrid,
  Columns,
  Truck,
  Upload,
  File,
  Paperclip,
  Scale,
  ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Contract types mapping with icons and descriptions
const CONTRACT_TYPES = [
  { value: 'employment', label: 'Trabalhista', color: 'bg-blue-100 text-blue-800', icon: Users, description: 'Contratos de trabalho e CLT' },
  { value: 'service', label: 'Prestação de Serviços', color: 'bg-green-100 text-green-800', icon: Briefcase, description: 'Serviços terceirizados e consultorias' },
  { value: 'supply', label: 'Fornecimento', color: 'bg-purple-100 text-purple-800', icon: Truck, description: 'Fornecimento de produtos e materiais' },
  { value: 'nda', label: 'NDA/Confidencialidade', color: 'bg-orange-100 text-orange-800', icon: Shield, description: 'Acordos de confidencialidade' },
  { value: 'partnership', label: 'Parceria', color: 'bg-cyan-100 text-cyan-800', icon: Globe, description: 'Parcerias comerciais e estratégicas' },
  { value: 'rental', label: 'Locação', color: 'bg-yellow-100 text-yellow-800', icon: Building, description: 'Locação de imóveis e equipamentos' },
  { value: 'other', label: 'Outros', color: 'bg-gray-100 text-gray-800', icon: FileText, description: 'Outros tipos de contrato' }
];

// Enhanced contract status mapping with workflow
const CONTRACT_STATUS = [
  { value: 'draft', label: 'Rascunho', color: 'bg-gray-100 text-gray-800', icon: Edit, description: 'Contrato em elaboração', workflow: 0 },
  { value: 'under_review', label: 'Em Análise', color: 'bg-yellow-100 text-yellow-800', icon: Clock, description: 'Aguardando aprovação', workflow: 1 },
  { value: 'approved', label: 'Aprovado', color: 'bg-green-100 text-green-800', icon: CheckCircle, description: 'Aprovado para assinatura', workflow: 2 },
  { value: 'sent_for_signature', label: 'Enviado p/ Assinatura', color: 'bg-blue-100 text-blue-800', icon: Send, description: 'Enviado para assinatura eletrônica', workflow: 3 },
  { value: 'partially_signed', label: 'Parcialmente Assinado', color: 'bg-indigo-100 text-indigo-800', icon: FileSignature, description: 'Algumas partes assinaram', workflow: 4 },
  { value: 'signed', label: 'ASSINADO', color: 'bg-green-100 text-green-800', icon: Award, description: 'Totalmente assinado', workflow: 5 },
  { value: 'active', label: 'Ativo', color: 'bg-green-100 text-green-800', icon: PlayCircle, description: 'Contrato em vigência', workflow: 6 },
  { value: 'suspended', label: 'Suspenso', color: 'bg-orange-100 text-orange-800', icon: PauseCircle, description: 'Temporariamente suspenso', workflow: 7 },
  { value: 'expired', label: 'Vencido', color: 'bg-red-100 text-red-800', icon: StopCircle, description: 'Prazo de vigência expirado', workflow: 8 },
  { value: 'cancelled', label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle, description: 'Cancelado antes da assinatura', workflow: 9 },
  { value: 'terminated', label: 'Rescindido', color: 'bg-red-100 text-red-800', icon: Archive, description: 'Rescindido após vigência', workflow: 10 }
];

// Enhanced priority mapping
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa', color: 'bg-gray-100 text-gray-600', icon: 'bg-gray-400', description: 'Sem urgência' },
  { value: 'medium', label: 'Média', color: 'bg-blue-100 text-blue-600', icon: 'bg-blue-400', description: 'Prioridade normal' },
  { value: 'high', label: 'Alta', color: 'bg-orange-100 text-orange-600', icon: 'bg-orange-400', description: 'Requer atenção' },
  { value: 'urgent', label: 'Urgente', color: 'bg-red-100 text-red-600', icon: 'bg-red-400', description: 'Ação imediata necessária' }
];

// Currency options
const CURRENCY_OPTIONS = [
  { value: 'BRL', label: 'Real (R$)', symbol: 'R$' },
  { value: 'USD', label: 'Dólar ($)', symbol: '$' },
  { value: 'EUR', label: 'Euro (€)', symbol: '€' },
  { value: 'GBP', label: 'Libra (£)', symbol: '£' }
];

// Payment terms options
const PAYMENT_TERMS = [
  'À vista',
  '30 dias',
  '60 dias',
  '90 dias',
  'Parcelado em 2x',
  'Parcelado em 3x',
  'Parcelado em 6x',
  'Parcelado em 12x',
  'Mensal recorrente',
  'Trimestral',
  'Semestral',
  'Anual'
];

// Contract categories for better organization
const CONTRACT_CATEGORIES = [
  { id: 'commercial', label: 'Comercial', icon: Briefcase, types: ['service', 'supply', 'partnership'] },
  { id: 'hr', label: 'Recursos Humanos', icon: Users, types: ['employment'] },
  { id: 'legal', label: 'Jurídico', icon: Shield, types: ['nda'] },
  { id: 'infrastructure', label: 'Infraestrutura', icon: Building, types: ['rental'] },
  { id: 'other', label: 'Outros', icon: FileText, types: ['other'] }
];

const ContractManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const allowedTabs = useMemo(() => {
    const allTabs = ['dashboard', 'approvals', 'signatures', 'renewals', 'compliance', 'reports'];
    return hasGranularPermission(user, 'juridico', 'contratos') ? allTabs : [];
  }, [user]);

  // Paginação client-side para a grid principal de contratos
  // (declarado depois de `contracts`, mas hook é seguro sob React rules of hooks
  // pois é declarado dentro do mesmo escopo de função)

  // Data states
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [stats, setStats] = useState({
    total_contracts: 0,
    active_contracts: 0,
    pending_approval: 0,
    expiring_soon: 0,
    total_value: 0,
    by_status: {},
    by_type: {},
    by_priority: {},
    monthly_values: [],
    expiring_contracts: []
  });

  // Advanced states
  const [notifications, setNotifications] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [contractHistory, setContractHistory] = useState([]);
  const [linkedProcesses, setLinkedProcesses] = useState([]);
  const [loadingLinkedProcesses, setLoadingLinkedProcesses] = useState(false);
  
  // Access control states
  const [userProfiles, setUserProfiles] = useState([]);
  const [users, setUsers] = useState([]);

  // Paginação client-side da grid principal de contratos
  const {
    paginated: contractsPaginated,
    pagination: contractsPagination,
    setPage: setContractsPage,
  } = useClientPagination(contracts, 10);

  // Form states  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    contract_type: 'service',
    description: '',
    contract_value: '',
    currency: 'BRL',
    payment_terms: '',
    start_date: '',
    end_date: '',
    auto_renewal: false,
    notice_period_days: '',
    priority: 'medium',
    contractor_id: 'company-default',
    contracted_parties: [],
    tags: [],
    notes: '',
    requires_approval: true,
    alert_days_before_expiry: 30,
    // New fields following the detailed structure
    category: '',
    responsible_person: '',
    department: '',
    project_code: '',
    budget_code: '',
    renewal_terms: '',
    termination_clause: '',
    penalty_clause: '',
    sla_requirements: '',
    compliance_requirements: [],
    approval_workflow: [],
    milestone_dates: [],
    payment_schedule: [],
    document_attachments: [],
    legal_review_required: true,
    finance_review_required: false,
    technical_review_required: false,
    // Access Control (Hybrid Model)
    allowed_profiles: [],
    allowed_users: []
  });

  // Enhanced filter states
  const [filters, setFilters] = useState({
    status: 'all',
    contract_type: 'all',
    priority: 'all',
    category: 'all',
    search: '',
    date_range: { start: '', end: '' },
    value_range: { min: '', max: '' },
    tags: [],
    expiring_in_days: 'all',
    assigned_to: 'all',
    approval_status: 'all',
    signature_status: 'all'
  });

  // View and interaction states
  const [currentView, setCurrentView] = useState('list'); // list, grid, kanban
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [submitting, setSubmitting] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: true,
    whatsapp_enabled: false,
    days_before_expiry: [30, 15, 7, 1],
    renewal_reminders: true,
    approval_notifications: true
  });

  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    loadDashboardData();
    loadUserProfiles();
    loadUsers();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (isMounted) {
      loadContracts();
    }
  }, [filters, isMounted]);

  const loadDashboardData = async () => {
    if (!isMounted) return;
    
    try {
      setLoading(true);
      await Promise.all([
        loadStats(),
        loadContracts()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (isMounted) {
        toast.error('Erro ao carregar dados do dashboard');
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/contracts/stats/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const loadContracts = async () => {
    if (!isMounted) return;
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.contract_type && filters.contract_type !== 'all') params.append('contract_type', filters.contract_type);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/api/contracts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (isMounted) {
        setContracts(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
      if (isMounted) {
        toast.error('Erro ao carregar contratos');
      }
    }
  };

  // Load user profiles for access control
  const loadUserProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/user-profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfiles(response.data.filter(profile => profile.is_active));
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
    }
  };

  // Load users for access control
  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(user => user.is_active));
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  // Load legal processes ("processos jurídicos") linked to a contract
  const loadLinkedProcesses = async (contractId) => {
    if (!contractId) return;
    try {
      setLoadingLinkedProcesses(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/contracts/${contractId}/processos-juridicos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkedProcesses(response.data);
    } catch (error) {
      console.error('Erro ao carregar processos vinculados:', error);
      setLinkedProcesses([]);
    } finally {
      setLoadingLinkedProcesses(false);
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // First, upload document if selected
      let documentId = null;
      if (selectedFile) {
        try {
          documentId = await handleDocumentUpload();
          if (!documentId) {
            toast.warning('Falha no upload do documento, mas o contrato será criado sem documento anexado');
          }
        } catch (uploadError) {
          console.error('Error uploading document:', uploadError);
          toast.warning('Erro no upload do documento. Contrato será criado sem documento anexado.');
          documentId = null;
        }
      }
      
      // Validar valor negativo
      const parsedValue = formData.contract_value ? parseFloat(formData.contract_value) : null;
      if (parsedValue !== null && parsedValue < 0) {
        toast.error('O valor do contrato não pode ser negativo');
        setSubmitting(false);
        return;
      }

      // Validar datas obrigatórias
      if (!formData.start_date || !formData.end_date) {
        toast.error('Data de início e data de término são obrigatórias');
        setSubmitting(false);
        return;
      }
      if (!validateDateBR(formData.start_date, { pastYears: 15, futureYears: 15 }) || !validateDateBR(formData.end_date, { pastYears: 15, futureYears: 15 })) {
        toast.error('Data de início ou término inválida');
        setSubmitting(false);
        return;
      }
      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        toast.error('Data de término deve ser posterior à data de início');
        setSubmitting(false);
        return;
      }

      // Preparar dados
      const contractData = {
        ...formData,
        contract_value: parsedValue,
        notice_period_days: formData.notice_period_days ? parseInt(formData.notice_period_days) : null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        tags: formData.tags.filter(tag => tag.trim() !== ''),
        contracted_parties: ['default-party'],
        document_id: documentId
      };

      const response = await axios.post(`${API}/api/contracts`, contractData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Contrato criado com sucesso!');
      
      if (documentId) {
        toast.success('Documento vinculado ao contrato e pronto para assinatura eletrônica!');
      }
      
      // Reset form state first
      resetForm();
      removeSelectedFile();
      
      // Close modal
      setShowCreateModal(false);
      
      // Reload data after a small delay to prevent React DOM conflicts
      setTimeout(async () => {
        await loadDashboardData();
      }, 100);

    } catch (error) {
      console.error('Erro ao criar contrato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar contrato');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditContract = async (e) => {
    e.preventDefault();
    
    if (!selectedContract) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // First, handle document upload if new file selected
      let newDocumentId = null;
      if (selectedFile) {
        try {
          newDocumentId = await handleDocumentUpload();
          if (!newDocumentId) {
            toast.warning('Falha no upload do documento, mas o contrato será atualizado sem documento anexado');
          }
        } catch (uploadError) {
          console.error('Error uploading document:', uploadError);
          toast.warning('Erro no upload do documento. Contrato será atualizado sem documento anexado.');
          newDocumentId = null;
        }
      }
      
      // Validate negative value
      const parsedValue = formData.contract_value ? parseFloat(formData.contract_value) : null;
      if (parsedValue !== null && parsedValue < 0) {
        toast.error('O valor do contrato não pode ser negativo');
        setSubmitting(false);
        return;
      }

      // Validar datas obrigatórias
      if (!formData.start_date || !formData.end_date) {
        toast.error('Data de início e data de término são obrigatórias');
        setSubmitting(false);
        return;
      }
      if (!validateDateBR(formData.start_date, { pastYears: 15, futureYears: 15 }) || !validateDateBR(formData.end_date, { pastYears: 15, futureYears: 15 })) {
        toast.error('Data de início ou término inválida');
        setSubmitting(false);
        return;
      }
      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        toast.error('Data de término deve ser posterior à data de início');
        setSubmitting(false);
        return;
      }

      // Prepare update data
      const updateData = {
        ...formData,
        contract_value: parsedValue,
        notice_period_days: formData.notice_period_days ? parseInt(formData.notice_period_days) : null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        tags: formData.tags.filter(tag => tag.trim() !== '')
      };

      // Handle document_id: either new document or preserve existing
      if (newDocumentId) {
        updateData.document_id = newDocumentId;
      } else if (selectedContract.document_id) {
        updateData.document_id = selectedContract.document_id;
      }

      // Update contract
      await axios.put(`${API}/api/contracts/${selectedContract.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Contrato atualizado com sucesso!');
      
      if (newDocumentId) {
        toast.success('Novo documento vinculado ao contrato e pronto para assinatura eletrônica!');
      }
      
      // Close modal and clean up state BEFORE reloading data
      setShowEditModal(false);
      setSelectedContract(null);
      resetForm();
      removeSelectedFile();
      
      // Reload data with delay to prevent DOM conflicts
      setTimeout(async () => {
        if (isMounted) {
          await loadDashboardData();
        }
      }, 300);

    } catch (error) {
      console.error('Erro ao atualizar contrato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar contrato');
    } finally {
      if (isMounted) {
        setSubmitting(false);
      }
    }
  };

  const handleDeleteContract = async (contractId, contractTitle) => {
    if (!window.confirm(`Tem certeza que deseja excluir o contrato "${contractTitle}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Contrato excluído com sucesso!');
      await loadDashboardData();

    } catch (error) {
      console.error('Erro ao excluir contrato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir contrato');
    }
  };

  const handleSendForSignature = async (contractId, contractTitle) => {
    if (!window.confirm(`Enviar o contrato "${contractTitle}" para assinatura eletrônica?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/contracts/${contractId}/send-for-signature`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Contrato enviado para assinatura com sucesso!');
      await loadDashboardData();

    } catch (error) {
      console.error('Erro ao enviar para assinatura:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar para assinatura');
    }
  };

  const openEditModal = (contract) => {
    setSelectedContract(contract);
    setFormData({
      title: contract.title || '',
      contract_type: contract.contract_type || 'service',
      description: contract.description || '',
      contract_value: contract.contract_value || '',
      currency: contract.currency || 'BRL',
      payment_terms: contract.payment_terms || '',
      start_date: contract.start_date ? contract.start_date.split('T')[0] : '',
      end_date: contract.end_date ? contract.end_date.split('T')[0] : '',
      auto_renewal: contract.auto_renewal || false,
      notice_period_days: contract.notice_period_days || '',
      priority: contract.priority || 'medium',
      contractor_id: contract.contractor_id || 'company-default',
      contracted_parties: contract.contracted_parties || [],
      tags: contract.tags || [],
      notes: contract.notes || '',
      requires_approval: contract.requires_approval !== false,
      alert_days_before_expiry: contract.alert_days_before_expiry || 30,
      // Access Control fields
      allowed_profiles: contract.allowed_profiles || [],
      allowed_users: contract.allowed_users || []
    });
    setShowEditModal(true);
  };

  const openViewModal = (contract) => {
    setSelectedContract(contract);
    setShowViewModal(true);
    setLinkedProcesses([]);
    loadLinkedProcesses(contract.id);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      contract_type: 'service',
      description: '',
      contract_value: '',
      currency: 'BRL',
      payment_terms: '',
      start_date: '',
      end_date: '',
      auto_renewal: false,
      notice_period_days: '',
      priority: 'medium',
      contractor_id: 'company-default',
      contracted_parties: [],
      tags: [],
      notes: '',
      requires_approval: true,
      alert_days_before_expiry: 30,
      // Access Control fields
      allowed_profiles: [],
      allowed_users: []
    });
    removeSelectedFile();
  };

  const getStatusConfig = (status) => {
    return CONTRACT_STATUS.find(s => s.value === status) || CONTRACT_STATUS[0];
  };

  const getTypeConfig = (type) => {
    return CONTRACT_TYPES.find(t => t.value === type) || CONTRACT_TYPES[0];
  };

  const getPriorityConfig = (priority) => {
    return PRIORITY_OPTIONS.find(p => p.value === priority) || PRIORITY_OPTIONS[1];
  };

  const getProcessStatusColor = (status) => {
    const map = {
      'Ativo': 'bg-blue-100 text-blue-800',
      'Suspenso': 'bg-yellow-100 text-yellow-800',
      'Encerrado': 'bg-gray-100 text-gray-800',
      'Arquivado': 'bg-gray-100 text-gray-600'
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const getProcessRiskColor = (risco) => {
    const map = {
      'Baixo': 'bg-green-100 text-green-800',
      'Médio': 'bg-orange-100 text-orange-800',
      'Alto': 'bg-red-100 text-red-800'
    };
    return map[risco] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value, currency = 'BRL') => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const handleTagAdd = (tagInput) => {
    const newTag = tagInput.trim();
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }));
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // New handlers for advanced functionality
  const handleApproveContract = async (contractId) => {
    if (!window.confirm('Aprovar este contrato?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/contracts/${contractId}`, 
        { status: 'approved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contrato aprovado com sucesso!');
      await loadDashboardData();
    } catch (error) {
      console.error('Erro ao aprovar contrato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao aprovar contrato');
    }
  };

  const handleRejectContract = async (contractId) => {
    const reason = window.prompt('Motivo da rejeição:');
    if (!reason) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/contracts/${contractId}`, 
        { 
          status: 'draft',
          rejection_reason: reason,
          notes: `Rejeitado: ${reason}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contrato rejeitado!');
      await loadDashboardData();
    } catch (error) {
      console.error('Erro ao rejeitar contrato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar contrato');
    }
  };

  const handleCheckSignatureStatus = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/contracts/${contractId}/signature-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Status da assinatura: ${response.data.status}`);
      await loadDashboardData();
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      toast.error('Erro ao verificar status da assinatura');
    }
  };

  const handleResendSignature = async (contractId) => {
    if (!window.confirm('Reenviar convite para assinatura?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/contracts/${contractId}/resend-signature`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Convite reenviado com sucesso!');
    } catch (error) {
      console.error('Erro ao reenviar convite:', error);
      toast.error('Erro ao reenviar convite para assinatura');
    }
  };

  const handleRenewContract = async (contractId) => {
    if (!window.confirm('Renovar este contrato?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/contracts/${contractId}/renew`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contrato renovado com sucesso!');
      await loadDashboardData();
    } catch (error) {
      console.error('Erro ao renovar contrato:', error);
      toast.error('Erro ao renovar contrato');
    }
  };

  const handleSendRenewalReminder = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/contracts/${contractId}/renewal-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Lembrete de renovação enviado!');
    } catch (error) {
      console.error('Erro ao enviar lembrete:', error);
      toast.error('Erro ao enviar lembrete de renovação');
    }
  };

  const handleRequestLegalReview = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/contracts/${contractId}/request-legal-review`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Revisão legal solicitada!');
      await loadDashboardData();
    } catch (error) {
      console.error('Erro ao solicitar revisão legal:', error);
      toast.error('Erro ao solicitar revisão legal');
    }
  };

  const handleMarkCompliant = async (contractId) => {
    if (!window.confirm('Marcar este contrato como conforme?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/contracts/${contractId}`, 
        { 
          legal_reviewed: true,
          legal_review_date: new Date().toISOString(),
          compliance_status: 'compliant'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contrato marcado como conforme!');
      await loadDashboardData();
    } catch (error) {
      console.error('Erro ao marcar como conforme:', error);
      toast.error('Erro ao marcar contrato como conforme');
    }
  };

  // Function to view document
  const handleViewDocument = async (contract) => {
    if (!contract.document_id) {
      toast.error('Este contrato não possui documento anexado');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Check if document is signed, if so get signed version
      const signatureResponse = await axios.get(`${API}/api/contracts/${contract.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const contractData = signatureResponse.data;
      
      // If contract has signed document, show signed version
      if (contractData.signature_status === 'signed' || contractData.signature_status === 'fully_signed') {
        // Try to get signed document
        try {
          const signedDocResponse = await axios.get(`${API}/api/signatures/documents/${contract.document_id}/download-signed`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          
          // Create blob and open signed document
          const blob = new Blob([signedDocResponse.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          window.URL.revokeObjectURL(url);
          return;
        } catch (signedError) {
          console.log('No signed document available, showing original');
        }
      }
      
      // Show original document - use correct endpoint for contract documents
      const documentResponse = await axios.get(`${API}/api/contracts/documents/${contract.document_id}/view`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([documentResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      toast.error('Erro ao abrir documento. Verifique se o documento existe.');
    }
  };

  // Function to view signed document
  const handleViewSignedDocument = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fazer request com autenticação para baixar o documento
      const response = await axios.get(`${API}/api/contracts/${contractId}/signed-document/view`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob' // Importante para arquivos
      });
      
      // Criar blob e abrir em nova aba
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Limpar URL após um tempo para liberar memória
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
    } catch (error) {
      console.error('Erro ao visualizar documento assinado:', error);
      
      if (error.response?.status === 401) {
        toast.error('Não autorizado. Faça login novamente.');
      } else if (error.response?.status === 404) {
        toast.error('Documento assinado não encontrado.');
      } else if (error.response?.status === 400) {
        toast.error('Documento ainda não foi totalmente assinado.');
      } else {
        toast.error('Erro ao abrir documento assinado. Tente novamente.');
      }
    }
  };

  // Download document (always available)
  const handleDownloadDocument = async (contract) => {
    if (!contract.document_id) {
      toast.error('Este contrato não possui documento anexado');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/contracts/documents/${contract.document_id}/view`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const baseName = (contract.title || 'documento').replace(/[^\w\s.-]/g, '').trim();
      link.setAttribute('download', `${baseName || 'documento'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Download do documento iniciado!');
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      let errorMessage = 'Erro ao baixar documento';
      if (error.response?.status === 401) {
        errorMessage = 'Sem autorização para download. Faça login novamente.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Sem permissão para baixar este documento.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Documento não encontrado.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    }
  };

  // Document upload functions
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 30 * 1024 * 1024; // 30MB

      if (!allowedTypes.includes(file.type)) {
        toast.error('Tipo de arquivo não suportado. Use PDF, DOC ou DOCX.');
        return;
      }

      if (file.size > maxSize) {
        toast.error(`Arquivo muito grande. O tamanho máximo permitido é 30MB. Seu arquivo tem ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
        return;
      }

      setSelectedFile(file);
      toast.success(`Arquivo "${file.name}" selecionado com sucesso!`);
    }
  };

  const handleDocumentUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecione um arquivo primeiro');
      return null;
    }

    try {
      setUploadingDocument(true);
      const token = localStorage.getItem('token');
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('title', formData.title || 'Documento do Contrato');
      uploadFormData.append('description', 'Documento associado ao contrato');

      const response = await axios.post(`${API}/api/signatures/documents/upload`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Documento enviado com sucesso!');
      return response.data.document.id; // Return document ID for linking to contract

    } catch (error) {
      console.error('Erro ao fazer upload do documento:', error);
      toast.error(error.response?.data?.detail || 'Erro ao fazer upload do documento');
      return null;
    } finally {
      setUploadingDocument(false);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    // Clear file input
    const fileInput = document.getElementById('document-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileContract className="h-8 w-8 text-blue-600" />
            Gestão de Contratos
          </h1>
          <p className="text-gray-600 mt-2">
            Gerencie contratos e envie para assinatura eletrônica
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            onClick={loadDashboardData}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Button>
          <Button 
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Contrato
          </Button>
        </div>
      </div>

      {/* Enhanced Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
        {/* Total Contracts */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total de Contratos</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total_contracts}</p>
                <p className="text-xs text-green-600 mt-1">+12% este mês</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileContract className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Contracts */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Contratos Ativos</p>
                <p className="text-2xl font-bold text-green-600">{stats.active_contracts}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {stats.total_contracts > 0 ? Math.round((stats.active_contracts / stats.total_contracts) * 100) : 0}% do total
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <PlayCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Approval */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pendente Aprovação</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending_approval}</p>
                <div className="flex items-center mt-1">
                  <Clock className="h-3 w-3 text-yellow-600 mr-1" />
                  <p className="text-xs text-yellow-600">Requer ação</p>
                </div>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expiring Soon */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Vencendo em 30d</p>
                <p className="text-2xl font-bold text-orange-600">{stats.expiring_soon}</p>
                <div className="flex items-center mt-1">
                  <BellRing className="h-3 w-3 text-orange-600 mr-1" />
                  <p className="text-xs text-orange-600">Alertas ativos</p>
                </div>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Value */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Valor Total</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(stats.total_value)}
                </p>
                <p className="text-xs text-purple-600 mt-1">Portfólio ativo</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signature Status */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">P/ Assinatura</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {stats.by_status?.sent_for_signature || 0}
                </p>
                <p className="text-xs text-indigo-600 mt-1">Aguardando</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileSignature className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats and Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Workflow Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              Status dos Contratos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {CONTRACT_STATUS.slice(0, 6).map(status => {
              const count = stats.by_status?.[status.value] || 0;
              const percentage = stats.total_contracts > 0 ? (count / stats.total_contracts) * 100 : 0;
              const StatusIcon = status.icon;
              
              return (
                <div key={status.value} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusIcon className="h-4 w-4 text-gray-600" />
                    <span className="text-sm">{status.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={percentage} className="w-16 h-2" />
                    <span className="text-sm font-medium w-8">{count}</span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Contract Types Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5 text-green-600" />
              Tipos de Contrato
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {CONTRACT_TYPES.slice(0, 5).map(type => {
              const count = stats.by_type?.[type.value] || 0;
              const percentage = stats.total_contracts > 0 ? (count / stats.total_contracts) * 100 : 0;
              const TypeIcon = type.icon;
              
              return (
                <div key={type.value} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TypeIcon className="h-4 w-4 text-gray-600" />
                    <span className="text-sm">{type.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={percentage} className="w-16 h-2" />
                    <span className="text-sm font-medium w-8">{count}</span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Recent Activity & Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-600" />
              Alertas e Atividades
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Critical alerts */}
            {stats.expiring_soon > 0 && (
              <div className="p-2 bg-orange-50 border border-orange-200 rounded-md">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    {stats.expiring_soon} contratos vencendo
                  </span>
                </div>
                <p className="text-xs text-orange-600 ml-6">Próximos 30 dias</p>
              </div>
            )}
            
            {stats.pending_approval > 0 && (
              <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    {stats.pending_approval} aguardando aprovação
                  </span>
                </div>
                <p className="text-xs text-yellow-600 ml-6">Requer revisão</p>
              </div>
            )}

            {(stats.by_status?.sent_for_signature || 0) > 0 && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center gap-2">
                  <FileSignature className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">
                    {stats.by_status.sent_for_signature} aguardando assinatura
                  </span>
                </div>
                <p className="text-xs text-blue-600 ml-6">Em processo</p>
              </div>
            )}

            {/* Quick actions */}
            <div className="pt-2 border-t space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => setShowNotificationModal(true)}
              >
                <Bell className="h-4 w-4 mr-2" />
                Configurar Alertas
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => setShowReportsModal(true)}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Relatórios
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          {allowedTabs.includes('dashboard') && <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <FileContract className="h-4 w-4" />
            Contratos
          </TabsTrigger>}
          {allowedTabs.includes('approvals') && <TabsTrigger value="approvals" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Aprovações
          </TabsTrigger>}
          {allowedTabs.includes('signatures') && <TabsTrigger value="signatures" className="flex items-center gap-2">
            <FileSignature className="h-4 w-4" />
            Assinaturas
          </TabsTrigger>}
          {allowedTabs.includes('renewals') && <TabsTrigger value="renewals" className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            Renovações
          </TabsTrigger>}
          {allowedTabs.includes('compliance') && <TabsTrigger value="compliance" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Compliance
          </TabsTrigger>}
          {allowedTabs.includes('reports') && <TabsTrigger value="reports" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Relatórios
          </TabsTrigger>}
        </TabsList>

        {/* Enhanced Contracts Tab */}
        {allowedTabs.includes('dashboard') && (
        <TabsContent value="dashboard">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileContract className="h-5 w-5" />
                    Gestão de Contratos
                  </CardTitle>
                  <CardDescription>
                    Controle completo do ciclo de vida dos contratos
                  </CardDescription>
                </div>
                
                {/* View Controls */}
                <div className="flex items-center space-x-2">
                  <div className="flex items-center border rounded-lg p-1">
                    <Button
                      variant={currentView === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCurrentView('list')}
                      className="px-2"
                    >
                      <Table className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={currentView === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCurrentView('grid')}
                      className="px-2"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={currentView === 'kanban' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCurrentView('kanban')}
                      className="px-2"
                    >
                      <Columns className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExportLoading(true)}
                    disabled={exportLoading}
                  >
                    {exportLoading ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Exportar
                  </Button>
                </div>
              </div>
              
              {/* Advanced Filters */}
              <div className="space-y-4 mt-6">
                {/* Primary Filters */}
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar por título, número ou descrição..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="w-80"
                    />
                  </div>
                  
                  <Select
                    value={filters.status}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Status do Contrato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Status</SelectItem>
                      {CONTRACT_STATUS.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          <div className="flex items-center gap-2">
                            <status.icon className="h-4 w-4" />
                            {status.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={filters.contract_type}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, contract_type: value }))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Tipo de Contrato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Tipos</SelectItem>
                      {CONTRACT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={filters.priority}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Prioridade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {PRIORITY_OPTIONS.map(priority => (
                        <SelectItem key={priority.value} value={priority.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${priority.icon}`}></div>
                            {priority.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Filtros Avançados
                  </Button>
                </div>

                {/* Advanced Filters Panel */}
                {showAdvancedFilters && (
                  <Card className="p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Date Range */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Período de Vigência</Label>
                        <div className="flex gap-2">
                          <Input
                            type="date"
                            placeholder="Data início"
                            value={filters.date_range.start}
                            onChange={(e) => setFilters(prev => ({
                              ...prev,
                              date_range: { ...prev.date_range, start: e.target.value }
                            }))}
                            className="text-xs"
                          />
                          <Input
                            type="date"
                            placeholder="Data fim"
                            value={filters.date_range.end}
                            onChange={(e) => setFilters(prev => ({
                              ...prev,
                              date_range: { ...prev.date_range, end: e.target.value }
                            }))}
                            className="text-xs"
                          />
                        </div>
                      </div>

                      {/* Value Range */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Faixa de Valor</Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            placeholder="Valor mín."
                            value={filters.value_range.min}
                            onChange={(e) => setFilters(prev => ({
                              ...prev,
                              value_range: { ...prev.value_range, min: e.target.value }
                            }))}
                            className="text-xs"
                          />
                          <Input
                            type="number"
                            placeholder="Valor máx."
                            value={filters.value_range.max}
                            onChange={(e) => setFilters(prev => ({
                              ...prev,
                              value_range: { ...prev.value_range, max: e.target.value }
                            }))}
                            className="text-xs"
                          />
                        </div>
                      </div>

                      {/* Expiring Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Vencimento</Label>
                        <Select
                          value={filters.expiring_in_days}
                          onValueChange={(value) => setFilters(prev => ({ ...prev, expiring_in_days: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Período de vencimento" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="7">Próximos 7 dias</SelectItem>
                            <SelectItem value="15">Próximos 15 dias</SelectItem>
                            <SelectItem value="30">Próximos 30 dias</SelectItem>
                            <SelectItem value="60">Próximos 60 dias</SelectItem>
                            <SelectItem value="90">Próximos 90 dias</SelectItem>
                            <SelectItem value="expired">Já vencidos</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Quick Actions */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Ações Rápidas</Label>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFilters({
                              status: '',
                              contract_type: '',
                              priority: '',
                              category: '',
                              search: '',
                              date_range: { start: '', end: '' },
                              value_range: { min: '', max: '' },
                              tags: [],
                              expiring_in_days: '',
                              assigned_to: '',
                              approval_status: '',
                              signature_status: ''
                            })}
                          >
                            Limpar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFilters(prev => ({ ...prev, expiring_in_days: '30' }))}
                          >
                            Vencendo
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Active Filters Display */}
                {(filters.search || filters.status || filters.contract_type || filters.priority) && (
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-gray-600">Filtros ativos:</span>
                    {filters.search && (
                      <Badge variant="secondary" className="gap-1">
                        Busca: {filters.search}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1"
                          onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                        >
                          <XCircle className="h-3 w-3" />
                        </Button>
                      </Badge>
                    )}
                    {filters.status && (
                      <Badge variant="secondary" className="gap-1">
                        Status: {CONTRACT_STATUS.find(s => s.value === filters.status)?.label}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1"
                          onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
                        >
                          <XCircle className="h-3 w-3" />
                        </Button>
                      </Badge>
                    )}
                    {filters.contract_type && (
                      <Badge variant="secondary" className="gap-1">
                        Tipo: {CONTRACT_TYPES.find(t => t.value === filters.contract_type)?.label}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1"
                          onClick={() => setFilters(prev => ({ ...prev, contract_type: '' }))}
                        >
                          <XCircle className="h-3 w-3" />
                        </Button>
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <p>Carregando contratos...</p>
                </div>
              ) : (
                <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contrato</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Vigência</TableHead>
                      <TableHead>Prioridade</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contractsPaginated.map(contract => {
                      const statusConfig = getStatusConfig(contract.status);
                      const typeConfig = getTypeConfig(contract.contract_type);
                      const priorityConfig = getPriorityConfig(contract.priority);
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <TableRow key={contract.id}>
                          <TableCell>
                            <div>
                              <p 
                                className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer" 
                                onClick={() => handleViewDocument(contract)}
                                title="Clique para visualizar o documento"
                              >
                                {contract.title}
                              </p>
                              <p className="text-sm text-gray-500">
                                {contract.contract_number}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={typeConfig.color}>
                              {typeConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <StatusIcon className="h-4 w-4" />
                              <Badge className={statusConfig.color}>
                                {statusConfig.label}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            {formatCurrency(contract.contract_value, contract.currency)}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {formatDate(contract.start_date)} - {formatDate(contract.end_date)}
                              {contract.days_until_expiry !== null && contract.days_until_expiry <= 30 && (
                                <div className="text-orange-600 font-medium">
                                  {contract.days_until_expiry > 0 
                                    ? `${contract.days_until_expiry} dias restantes`
                                    : contract.is_expired ? 'Vencido' : 'Vence hoje'
                                  }
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={priorityConfig.color}>
                              {priorityConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openViewModal(contract)}
                                title="Visualizar contrato"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadDocument(contract)}
                                title="Baixar documento"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditModal(contract)}
                                title="Editar contrato"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendForSignature(contract.id, contract.title)}
                                className="text-blue-600 hover:text-blue-700"
                                title="Enviar para assinatura"
                              >
                                <FileSignature className="h-4 w-4" />
                              </Button>
                              
                              {/* Botão Visualizar Documento Assinado - NOVO */}
                              {(contract.status === 'signed' || contract.signature_status === 'fully_signed') && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewSignedDocument(contract.id)}
                                  className="text-green-600 hover:text-green-700"
                                  title="Visualizar documento assinado"
                                >
                                  <Award className="h-4 w-4" />
                                </Button>
                              )}
                              
                              {!['signed', 'active'].includes(contract.status) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteContract(contract.id, contract.title)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Excluir contrato"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <GridPagination pagination={contractsPagination} onPageChange={setContractsPage} />
                </>
              )}

              {contracts.length === 0 && !loading && (
                <div className="text-center py-12">
                  <FileContract className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum contrato encontrado</h3>
                  <p className="text-gray-500 mb-4">
                    Crie seu primeiro contrato para começar a gerenciar
                  </p>
                  <Button onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Primeiro Contrato
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Approvals Tab */}
        {allowedTabs.includes('approvals') && (
        <TabsContent value="approvals">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Workflow de Aprovações
              </CardTitle>
              <CardDescription>
                Gerencie aprovações pendentes e histórico de aprovações
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Pending Approvals */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-yellow-600" />
                    Aprovações Pendentes ({stats.pending_approval})
                  </h3>
                  
                  {stats.pending_approval > 0 ? (
                    <div className="grid gap-4">
                      {contracts.filter(c => c.status === 'under_review').slice(0, 5).map(contract => (
                        <Card key={contract.id} className="border-l-4 border-l-yellow-500">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold">{contract.title}</h4>
                                <p className="text-sm text-gray-600">{contract.contract_number}</p>
                                <div className="flex items-center gap-4 mt-2">
                                  <Badge className={getTypeConfig(contract.contract_type).color}>
                                    {getTypeConfig(contract.contract_type).label}
                                  </Badge>
                                  <span className="text-sm text-gray-500">
                                    Valor: {formatCurrency(contract.contract_value, contract.currency)}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    Criado: {formatDate(contract.created_at)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openViewModal(contract)}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Revisar
                                </Button>
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApproveContract(contract.id)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Aprovar
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleRejectContract(contract.id)}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Rejeitar
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>Nenhuma aprovação pendente</p>
                    </div>
                  )}
                </div>

                {/* Approval History */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <History className="h-5 w-5 text-blue-600" />
                    Histórico de Aprovações
                  </h3>
                  
                  <Card className="max-h-64 overflow-y-auto">
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {contracts.filter(c => ['approved', 'signed', 'active'].includes(c.status)).slice(0, 10).map(contract => (
                          <div key={contract.id} className="p-4 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{contract.title}</p>
                                <p className="text-sm text-gray-600">{contract.contract_number}</p>
                              </div>
                              <div className="text-right">
                                <Badge className="bg-green-100 text-green-800">Aprovado</Badge>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatDate(contract.approved_at || contract.created_at)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Signatures Tab */}
        {allowedTabs.includes('signatures') && (
        <TabsContent value="signatures">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-blue-600" />
                Gestão de Assinaturas
              </CardTitle>
              <CardDescription>
                Acompanhe o status de assinaturas eletrônicas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Signature Status Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Aguardando Assinatura</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {stats.by_status?.sent_for_signature || 0}
                          </p>
                        </div>
                        <Send className="h-8 w-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-orange-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Parcialmente Assinados</p>
                          <p className="text-2xl font-bold text-orange-600">
                            {stats.by_status?.partially_signed || 0}
                          </p>
                        </div>
                        <FileSignature className="h-8 w-8 text-orange-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Totalmente Assinados</p>
                          <p className="text-2xl font-bold text-green-600">
                            {stats.by_status?.signed || 0}
                          </p>
                        </div>
                        <Award className="h-8 w-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Contracts Waiting for Signature */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Send className="h-5 w-5 text-blue-600" />
                    Contratos Aguardando Assinatura
                  </h3>
                  
                  <div className="space-y-3">
                    {contracts.filter(c => c.status === 'sent_for_signature').map(contract => (
                      <Card key={contract.id} className="border-l-4 border-l-blue-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{contract.title}</h4>
                              <p className="text-sm text-gray-600">{contract.contract_number}</p>
                              <div className="flex items-center gap-4 mt-2">
                                <Badge className={getTypeConfig(contract.contract_type).color}>
                                  {getTypeConfig(contract.contract_type).label}
                                </Badge>
                                <span className="text-sm text-gray-500">
                                  Enviado: {formatDate(contract.signature_sent_at || contract.updated_at)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCheckSignatureStatus(contract.id)}
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Verificar Status
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResendSignature(contract.id)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Reenviar
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {contracts.filter(c => c.status === 'sent_for_signature').length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FileSignature className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>Nenhum contrato aguardando assinatura</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Renewals Tab */}
        {allowedTabs.includes('renewals') && (
        <TabsContent value="renewals">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-purple-600" />
                Gestão de Renovações
              </CardTitle>
              <CardDescription>
                Controle renovações automáticas e alertas de vencimento
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Renewal Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="border-l-4 border-l-red-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Vencendo (7 dias)</p>
                          <p className="text-2xl font-bold text-red-600">
                            {contracts.filter(c => c.days_until_expiry <= 7 && c.days_until_expiry >= 0).length}
                          </p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-red-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-orange-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Vencendo (30 dias)</p>
                          <p className="text-2xl font-bold text-orange-600">
                            {stats.expiring_soon}
                          </p>
                        </div>
                        <Clock className="h-8 w-8 text-orange-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Renovação Automática</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {contracts.filter(c => c.auto_renewal).length}
                          </p>
                        </div>
                        <RotateCcw className="h-8 w-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-gray-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Já Vencidos</p>
                          <p className="text-2xl font-bold text-gray-600">
                            {contracts.filter(c => c.is_expired).length}
                          </p>
                        </div>
                        <StopCircle className="h-8 w-8 text-gray-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Contracts Expiring Soon */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                    Contratos Vencendo ({stats.expiring_soon})
                  </h3>
                  
                  <div className="space-y-3">
                    {contracts.filter(c => c.days_until_expiry !== null && c.days_until_expiry <= 30 && c.days_until_expiry >= 0).map(contract => (
                      <Card key={contract.id} className={`border-l-4 ${
                        contract.days_until_expiry <= 7 ? 'border-l-red-500' : 'border-l-orange-500'
                      }`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{contract.title}</h4>
                              <p className="text-sm text-gray-600">{contract.contract_number}</p>
                              <div className="flex items-center gap-4 mt-2">
                                <Badge className={getTypeConfig(contract.contract_type).color}>
                                  {getTypeConfig(contract.contract_type).label}
                                </Badge>
                                <span className={`text-sm font-medium ${
                                  contract.days_until_expiry <= 7 ? 'text-red-600' : 'text-orange-600'
                                }`}>
                                  {contract.days_until_expiry === 0 ? 'Vence hoje!' : 
                                   `${contract.days_until_expiry} dias restantes`}
                                </span>
                                <span className="text-sm text-gray-500">
                                  Vencimento: {formatDate(contract.end_date)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {contract.auto_renewal ? (
                                <Badge className="bg-blue-100 text-blue-800">
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  Auto-renovação
                                </Badge>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRenewContract(contract.id)}
                                >
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Renovar
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendRenewalReminder(contract.id)}
                              >
                                <Bell className="h-4 w-4 mr-1" />
                                Lembrete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {contracts.filter(c => c.days_until_expiry !== null && c.days_until_expiry <= 30 && c.days_until_expiry >= 0).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>Nenhum contrato vencendo nos próximos 30 dias</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Compliance Tab */}
        {allowedTabs.includes('compliance') && (
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-600" />
                Compliance e Auditoria
              </CardTitle>
              <CardDescription>
                Conformidade legal e trilha de auditoria
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Compliance Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Contratos Conformes</p>
                          <p className="text-2xl font-bold text-green-600">
                            {contracts.filter(c => ['signed', 'active'].includes(c.status)).length}
                          </p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-yellow-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Revisão Pendente</p>
                          <p className="text-2xl font-bold text-yellow-600">
                            {contracts.filter(c => c.legal_review_required && !c.legal_reviewed).length}
                          </p>
                        </div>
                        <Clock className="h-8 w-8 text-yellow-500" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-red-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Não Conformes</p>
                          <p className="text-2xl font-bold text-red-600">
                            {contracts.filter(c => c.compliance_issues?.length > 0).length}
                          </p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-red-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Compliance Actions Required */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    Ações de Compliance Necessárias
                  </h3>
                  
                  <div className="space-y-3">
                    {contracts.filter(c => c.legal_review_required && !c.legal_reviewed).slice(0, 5).map(contract => (
                      <Card key={contract.id} className="border-l-4 border-l-yellow-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{contract.title}</h4>
                              <p className="text-sm text-gray-600">{contract.contract_number}</p>
                              <div className="flex items-center gap-4 mt-2">
                                <Badge className="bg-yellow-100 text-yellow-800">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Revisão Legal Pendente
                                </Badge>
                                <span className="text-sm text-gray-500">
                                  Criado: {formatDate(contract.created_at)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRequestLegalReview(contract.id)}
                              >
                                <Shield className="h-4 w-4 mr-1" />
                                Solicitar Revisão
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMarkCompliant(contract.id)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Marcar Conforme
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {contracts.filter(c => c.legal_review_required && !c.legal_reviewed).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>Todos os contratos estão em conformidade</p>
                    </div>
                  )}
                </div>

                {/* Audit Trail */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <History className="h-5 w-5 text-blue-600" />
                    Trilha de Auditoria Recente
                  </h3>
                  
                  <Card className="max-h-64 overflow-y-auto">
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {contractHistory.slice(0, 10).map((entry, index) => (
                          <div key={index} className="p-4 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{entry.action}</p>
                                <p className="text-sm text-gray-600">{entry.description}</p>
                                <p className="text-xs text-gray-500">
                                  Por: {entry.performed_by_name} • {formatDate(entry.performed_at)}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {entry.contract_title}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Enhanced Reports Tab */}
        {allowedTabs.includes('reports') && (
        <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-600" />
                Relatórios e Analytics
              </CardTitle>
              <CardDescription>
                Análises detalhadas e insights sobre contratos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Report Types */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 hover:border-blue-300">
                    <CardContent className="p-6 text-center">
                      <PieChart className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Distribuição por Tipo</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Análise dos tipos de contratos mais utilizados
                      </p>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Gerar Relatório
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 hover:border-green-300">
                    <CardContent className="p-6 text-center">
                      <TrendingUp className="h-12 w-12 text-green-600 mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Evolução Temporal</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Crescimento e tendências ao longo do tempo
                      </p>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Gerar Relatório
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 hover:border-purple-300">
                    <CardContent className="p-6 text-center">
                      <DollarSign className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Análise Financeira</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Valores, ROI e impacto financeiro
                      </p>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Gerar Relatório
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 hover:border-orange-300">
                    <CardContent className="p-6 text-center">
                      <Activity className="h-12 w-12 text-orange-600 mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Performance</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Tempos de aprovação e assinatura
                      </p>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Gerar Relatório
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 hover:border-red-300">
                    <CardContent className="p-6 text-center">
                      <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Riscos e Alertas</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Contratos com riscos e vencimentos
                      </p>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Gerar Relatório
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 hover:border-indigo-300">
                    <CardContent className="p-6 text-center">
                      <Shield className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Compliance</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Status de conformidade e auditoria
                      </p>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Gerar Relatório
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Statistics */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Estatísticas Rápidas</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{stats.total_contracts}</p>
                      <p className="text-sm text-gray-600">Total de Contratos</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{stats.active_contracts}</p>
                      <p className="text-sm text-gray-600">Contratos Ativos</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">
                        {formatCurrency(stats.total_value)}
                      </p>
                      <p className="text-sm text-gray-600">Valor Total</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-600">
                        {stats.total_contracts > 0 ? Math.round((stats.active_contracts / stats.total_contracts) * 100) : 0}%
                      </p>
                      <p className="text-sm text-gray-600">Taxa de Ativação</p>
                    </div>
                  </div>
                </div>

                {/* Export Options */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Exportações</h3>
                  <div className="flex flex-wrap gap-4">
                    <Button variant="outline" className="flex items-center gap-2">
                      <FileDown className="h-4 w-4" />
                      Exportar todos os contratos (Excel)
                    </Button>
                    <Button variant="outline" className="flex items-center gap-2">
                      <FileDown className="h-4 w-4" />
                      Exportar contratos ativos (PDF)
                    </Button>
                    <Button variant="outline" className="flex items-center gap-2">
                      <FileDown className="h-4 w-4" />
                      Relatório de vencimentos (CSV)
                    </Button>
                    <Button variant="outline" className="flex items-center gap-2">
                      <FileDown className="h-4 w-4" />
                      Dashboard executivo (PDF)
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Modals will be added in the next part due to size limits */}

      {/* Create Contract Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Criar Novo Contrato</DialogTitle>
            <DialogDescription>
              Preencha as informações do contrato
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateContract}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Informações Básicas</h3>
                
                <div>
                  <Label htmlFor="title">Título do Contrato *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ex: Contrato de Prestação de Serviços"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="contract_type">Tipo de Contrato *</Label>
                  <select
                    id="contract_type"
                    value={formData.contract_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {CONTRACT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descreva o objeto do contrato"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="priority">Prioridade</Label>
                  <select
                    id="priority"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PRIORITY_OPTIONS.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Financial and Terms */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Valores e Prazos</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contract_value">Valor do Contrato</Label>
                    <CurrencyInput
                      id="contract_value"
                      value={formData.contract_value}
                      onChange={v => setFormData(prev => ({ ...prev, contract_value: v }))}
                      placeholder="0,00"
                      className="currency-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="currency">Moeda</Label>
                    <select
                      id="currency"
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="BRL">Real (BRL)</option>
                      <option value="USD">Dólar (USD)</option>
                      <option value="EUR">Euro (EUR)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="payment_terms">Condições de Pagamento</Label>
                  <Input
                    id="payment_terms"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                    placeholder="Ex: 30 dias após vencimento"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Data de Início *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date">Data de Término *</Label>
                    <Input
                      id="end_date"
                      type="date"
                      required
                      value={formData.end_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notice_period_days">Período de Aviso Prévio (dias)</Label>
                  <Input
                    id="notice_period_days"
                    type="number"
                    value={formData.notice_period_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, notice_period_days: e.target.value }))}
                    placeholder="30"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="auto_renewal"
                    checked={formData.auto_renewal}
                    onChange={(e) => setFormData(prev => ({ ...prev, auto_renewal: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="auto_renewal">Renovação Automática</Label>
                </div>
              </div>
            </div>

            {/* Document Upload Section */}
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <Upload className="h-5 w-5 text-blue-600" />
                Upload do Documento
              </h3>
              <p className="text-sm text-gray-600">
                Faça upload do documento para integração com o módulo de assinatura eletrônica
              </p>
              <p className="text-xs text-gray-500 mt-1">
                📎 Formatos aceitos: PDF, DOC, DOCX | Tamanho máximo: 30MB
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                {selectedFile ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-3 text-green-600">
                      <File className="h-8 w-8" />
                      <div className="text-left">
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={removeSelectedFile}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remover
                      </Button>
                      <Label htmlFor="document-upload" className="cursor-pointer">
                        <Button type="button" variant="outline" size="sm" asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-1" />
                            Trocar Arquivo
                          </span>
                        </Button>
                      </Label>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                    <div>
                      <Label htmlFor="document-upload" className="cursor-pointer">
                        <Button type="button" variant="outline" asChild>
                          <span>
                            <Paperclip className="h-4 w-4 mr-2" />
                            Selecionar Documento
                          </span>
                        </Button>
                      </Label>
                      <p className="text-xs text-gray-500 mt-2">
                        PDF, DOC ou DOCX - Máximo 10MB
                      </p>
                    </div>
                  </div>
                )}
                <input
                  id="document-upload"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {selectedFile && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-2">
                    <FileSignature className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">Integração com Assinatura Eletrônica</p>
                      <p className="text-blue-700">
                        O documento será automaticamente vinculado ao contrato e estará disponível 
                        para envio para assinatura eletrônica após a criação.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Additional Options */}
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-medium">Opções Adicionais</h3>
              
              <div>
                <Label htmlFor="tags">Tags (separadas por vírgula)</Label>
                <Input
                  id="tags"
                  value={formData.tags.join(', ')}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag) 
                  }))}
                  placeholder="Ex: importante, cliente-premium, recorrente"
                />
              </div>

              <div>
                <Label htmlFor="notes">Observações</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Observações internas sobre o contrato"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="requires_approval"
                    checked={formData.requires_approval}
                    onChange={(e) => setFormData(prev => ({ ...prev, requires_approval: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="requires_approval">Requer Aprovação</Label>
                </div>

                <div>
                  <Label htmlFor="alert_days_before_expiry">Alertar antes do vencimento (dias)</Label>
                  <Input
                    id="alert_days_before_expiry"
                    type="number"
                    value={formData.alert_days_before_expiry}
                    onChange={(e) => setFormData(prev => ({ ...prev, alert_days_before_expiry: parseInt(e.target.value) || 30 }))}
                    placeholder="30"
                  />
                </div>
              </div>
            </div>

            {/* Access Control Section - NEW */}
            <div className="mt-6 space-y-4 border-t pt-6">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-medium">Controle de Acesso (Híbrido)</h3>
              </div>
              <p className="text-sm text-gray-600">
                Define quem pode visualizar este contrato. Deixe ambos vazios para permitir acesso a todos os usuários com permissão de gerenciar contratos.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Allowed Profiles */}
                <div>
                  <Label htmlFor="allowed_profiles">Perfis Permitidos</Label>
                  <select
                    id="allowed_profiles"
                    multiple
                    value={formData.allowed_profiles}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({ ...prev, allowed_profiles: selectedOptions }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px]"
                  >
                    {userProfiles.map(profile => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.allowed_profiles.length > 0 
                      ? `${formData.allowed_profiles.length} perfil(is) selecionado(s)` 
                      : 'Segure Ctrl/Cmd para selecionar múltiplos'}
                  </p>
                </div>

                {/* Allowed Users */}
                <div>
                  <Label htmlFor="allowed_users">Usuários Permitidos</Label>
                  <select
                    id="allowed_users"
                    multiple
                    value={formData.allowed_users}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({ ...prev, allowed_users: selectedOptions }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px]"
                  >
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.allowed_users.length > 0 
                      ? `${formData.allowed_users.length} usuário(s) selecionado(s)` 
                      : 'Segure Ctrl/Cmd para selecionar múltiplos'}
                  </p>
                </div>
              </div>

              {/* Info Box */}
              {(formData.allowed_profiles.length > 0 || formData.allowed_users.length > 0) && (
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <div className="flex items-start gap-2">
                    <Shield className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-purple-900">Restrição de Acesso Ativa</p>
                      <p className="text-purple-700">
                        Este contrato será visível apenas para:
                        {formData.allowed_profiles.length > 0 && (
                          <span> usuários com perfil(is) selecionado(s)</span>
                        )}
                        {formData.allowed_profiles.length > 0 && formData.allowed_users.length > 0 && ' e'}
                        {formData.allowed_users.length > 0 && (
                          <span> os usuários individuais selecionados</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" type="button" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Contrato
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Contrato</DialogTitle>
            <DialogDescription>
              Modifique as informações do contrato
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditContract}>
            {/* Same form structure as create modal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Informações Básicas</h3>
                
                <div>
                  <Label htmlFor="edit_title">Título do Contrato *</Label>
                  <Input
                    id="edit_title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ex: Contrato de Prestação de Serviços"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="edit_contract_type">Tipo de Contrato *</Label>
                  <select
                    id="edit_contract_type"
                    value={formData.contract_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {CONTRACT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="edit_description">Descrição</Label>
                  <Textarea
                    id="edit_description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descreva o objeto do contrato"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="edit_priority">Prioridade</Label>
                  <select
                    id="edit_priority"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PRIORITY_OPTIONS.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Financial and Terms */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Valores e Prazos</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_contract_value">Valor do Contrato</Label>
                    <CurrencyInput
                      id="edit_contract_value"
                      value={formData.contract_value}
                      onChange={v => setFormData(prev => ({ ...prev, contract_value: v }))}
                      placeholder="0,00"
                      className="currency-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_currency">Moeda</Label>
                    <select
                      id="edit_currency"
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="BRL">Real (BRL)</option>
                      <option value="USD">Dólar (USD)</option>
                      <option value="EUR">Euro (EUR)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_payment_terms">Condições de Pagamento</Label>
                  <Input
                    id="edit_payment_terms"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                    placeholder="Ex: 30 dias após vencimento"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_start_date">Data de Início *</Label>
                    <Input
                      id="edit_start_date"
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_end_date">Data de Término *</Label>
                    <Input
                      id="edit_end_date"
                      type="date"
                      required
                      value={formData.end_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_notice_period_days">Período de Aviso Prévio (dias)</Label>
                  <Input
                    id="edit_notice_period_days"
                    type="number"
                    value={formData.notice_period_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, notice_period_days: e.target.value }))}
                    placeholder="30"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit_auto_renewal"
                    checked={formData.auto_renewal}
                    onChange={(e) => setFormData(prev => ({ ...prev, auto_renewal: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="edit_auto_renewal">Renovação Automática</Label>
                </div>
              </div>
            </div>

            {/* Document Upload Section */}
            <div className="mt-6 border-t pt-6">
              <h3 className="text-lg font-medium mb-4">Upload do Documento</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-document-upload">Atualizar Documento do Contrato</Label>
                  <Input
                    id="edit-document-upload"
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx"
                    className="cursor-pointer"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Formatos aceitos: PDF, DOC, DOCX (máx. 10MB)
                  </p>
                </div>
                
                {selectedFile && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center space-x-2">
                      <File className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">{selectedFile.name}</span>
                      <span className="text-xs text-blue-600">
                        ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeSelectedFile}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                <div className="text-sm text-gray-600 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">Integração com Assinatura Eletrônica</p>
                      <p className="text-yellow-700">
                        Se você atualizar o documento, um novo documento será criado no sistema de assinatura eletrônica.
                        O documento anterior permanecerá disponível no histórico.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Document Upload Section */}
            <div className="mt-6 border-t pt-6">
              <h3 className="text-lg font-medium mb-4">Upload do Documento</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-document-upload">Atualizar Documento do Contrato</Label>
                  <Input
                    id="edit-document-upload"
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx"
                    className="cursor-pointer"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Formatos aceitos: PDF, DOC, DOCX (máx. 10MB)
                  </p>
                </div>
                
                {selectedFile && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center space-x-2">
                      <File className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">{selectedFile.name}</span>
                      <span className="text-xs text-blue-600">
                        ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeSelectedFile}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                <div className="text-sm text-gray-600 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">Integração com Assinatura Eletrônica</p>
                      <p className="text-yellow-700">
                        Se você atualizar o documento, um novo documento será criado no sistema de assinatura eletrônica.
                        O documento anterior permanecerá disponível no histórico.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Access Control Section - NEW */}
            <div className="mt-6 space-y-4 border-t pt-6">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-medium">Controle de Acesso (Híbrido)</h3>
              </div>
              <p className="text-sm text-gray-600">
                Define quem pode visualizar este contrato. Deixe ambos vazios para permitir acesso a todos os usuários com permissão de gerenciar contratos.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Allowed Profiles */}
                <div>
                  <Label htmlFor="edit_allowed_profiles">Perfis Permitidos</Label>
                  <select
                    id="edit_allowed_profiles"
                    multiple
                    value={formData.allowed_profiles}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({ ...prev, allowed_profiles: selectedOptions }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px]"
                  >
                    {userProfiles.map(profile => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.allowed_profiles.length > 0 
                      ? `${formData.allowed_profiles.length} perfil(is) selecionado(s)` 
                      : 'Segure Ctrl/Cmd para selecionar múltiplos'}
                  </p>
                </div>

                {/* Allowed Users */}
                <div>
                  <Label htmlFor="edit_allowed_users">Usuários Permitidos</Label>
                  <select
                    id="edit_allowed_users"
                    multiple
                    value={formData.allowed_users}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({ ...prev, allowed_users: selectedOptions }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px]"
                  >
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.allowed_users.length > 0 
                      ? `${formData.allowed_users.length} usuário(s) selecionado(s)` 
                      : 'Segure Ctrl/Cmd para selecionar múltiplos'}
                  </p>
                </div>
              </div>

              {/* Info Box */}
              {(formData.allowed_profiles.length > 0 || formData.allowed_users.length > 0) && (
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <div className="flex items-start gap-2">
                    <Shield className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-purple-900">Restrição de Acesso Ativa</p>
                      <p className="text-purple-700">
                        Este contrato será visível apenas para:
                        {formData.allowed_profiles.length > 0 && (
                          <span> usuários com perfil(is) selecionado(s)</span>
                        )}
                        {formData.allowed_profiles.length > 0 && formData.allowed_users.length > 0 && ' e'}
                        {formData.allowed_users.length > 0 && (
                          <span> os usuários individuais selecionados</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" type="button" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4 mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Contract Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Contrato</DialogTitle>
            <DialogDescription>
              Visualização completa do contrato
            </DialogDescription>
          </DialogHeader>
          
          {selectedContract && (
            <div className="space-y-6">
              {/* Contract Header */}
              <div className="border-b pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{selectedContract.title}</h2>
                    <p className="text-gray-600">{selectedContract.contract_number}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {(() => {
                      const statusConfig = getStatusConfig(selectedContract.status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <div className="flex items-center gap-2">
                          <StatusIcon className="h-4 w-4" />
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Contract Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Tipo</Label>
                    <p className="mt-1">{getTypeConfig(selectedContract.contract_type).label}</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Descrição</Label>
                    <p className="mt-1">{selectedContract.description || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Prioridade</Label>
                    <div className="mt-1">
                      <Badge className={getPriorityConfig(selectedContract.priority).color}>
                        {getPriorityConfig(selectedContract.priority).label}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Valor</Label>
                    <p className="mt-1 text-lg font-semibold">
                      {formatCurrency(selectedContract.contract_value, selectedContract.currency)}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Vigência</Label>
                    <p className="mt-1">
                      {formatDate(selectedContract.start_date)} - {formatDate(selectedContract.end_date)}
                    </p>
                    {selectedContract.days_until_expiry !== null && (
                      <p className="text-sm text-orange-600">
                        {selectedContract.days_until_expiry > 0 
                          ? `${selectedContract.days_until_expiry} dias restantes`
                          : selectedContract.is_expired ? 'Vencido' : 'Vence hoje'
                        }
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Condições de Pagamento</Label>
                    <p className="mt-1">{selectedContract.payment_terms || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {selectedContract.tags && selectedContract.tags.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-600">Tags</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedContract.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedContract.notes && (
                <div>
                  <Label className="text-sm font-medium text-gray-600">Observações</Label>
                  <p className="mt-1 bg-gray-50 p-3 rounded-md">{selectedContract.notes}</p>
                </div>
              )}

              {/* Processos vinculados */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Scale className="h-4 w-4" />
                    Processos Jurídicos Vinculados
                    {linkedProcesses.length > 0 && (
                      <Badge variant="outline" className="ml-1">{linkedProcesses.length}</Badge>
                    )}
                  </Label>
                </div>

                {loadingLinkedProcesses ? (
                  <p className="text-sm text-gray-500">Carregando processos vinculados...</p>
                ) : linkedProcesses.length === 0 ? (
                  <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
                    Nenhum processo jurídico vinculado a este contrato.
                  </p>
                ) : (
                  <div className="border rounded-md divide-y">
                    {linkedProcesses.map((processo) => (
                      <div
                        key={processo.id}
                        className="flex items-center justify-between gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setShowViewModal(false);
                          navigate(`/juridico/processos/${processo.id}`);
                        }}
                      >
                        <div className="min-w-0">
                          <p className="font-medium truncate">{processo.titulo}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {processo.numero_processo || 'Sem número'} · {processo.tipo_processo}
                            {processo.valor_causa ? ` · ${formatCurrency(processo.valor_causa)}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={getProcessStatusColor(processo.status)}>{processo.status}</Badge>
                          <Badge className={getProcessRiskColor(processo.risco)}>Risco: {processo.risco}</Badge>
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Criado por:</span> {selectedContract.created_by_name || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Data de criação:</span> {formatDate(selectedContract.created_at)}
                </div>
                <div>
                  <span className="font-medium">Renovação automática:</span> {selectedContract.auto_renewal ? 'Sim' : 'Não'}
                </div>
                <div>
                  <span className="font-medium">Alerta de vencimento:</span> {selectedContract.alert_days_before_expiry} dias
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Fechar
            </Button>
            {selectedContract && (
              <Button onClick={() => {
                setShowViewModal(false);
                openEditModal(selectedContract);
              }}>
                <Edit className="h-4 w-4 mr-2" />
                Editar Contrato
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </div>
  );
};

export default ContractManagementPage;